/**
 * Mega Menu Nav Filter Web Component
 * Dynamically shows/hides menu content and promos based on sidebar item hover
 * 
 * Each sidebar item can have its own promotions via data-sidebar-promo attribute
 * matching the sidebar item's data-sidebar-index
 * 
 * Usage:
 * <mega-menu-nav-filter data-config='{"sidebarItems": [...]}'>
 *   <li class="mega-menu__nav-sidebar">...</li>
 *   <li class="mega-menu__nav-2-promo-wrapper" data-sidebar-promo="1">...</li>
 *   <div class="mega-menu__heading-items-list">...</div>
 * </mega-menu-nav-filter>
 */

class MegaMenuNavFilter extends HTMLElement {
  constructor() {
    super();
    this.config = null;
    this.activeSidebarIndex = null;
  }

  connectedCallback() {
    this.parseConfig();
    this.init();
  }

  parseConfig() {
    const configAttr = this.getAttribute('data-config');

    if (configAttr) {
      this.config = JSON.parse(configAttr);
    }
  }

  init() {
    const sidebar = this.querySelector('.mega-menu__nav-sidebar');

    if (!sidebar) {
      this.showAllContent();
      return;
    }

    this.attachEventListeners(sidebar);

    // Activate first sidebar item by default
    const firstLink = sidebar.querySelector('.mega-menu__nav-sidebar-link');
    if (firstLink) {
      const sidebarIndex = firstLink.getAttribute('data-sidebar-index');
      this.showContentForSidebarItem(sidebarIndex);
    }
  }

  attachEventListeners(sidebar) {
    const buttons = sidebar.querySelectorAll('.mega-menu__nav-sidebar-link');

    buttons.forEach(button => {
      const sidebarIndex = button.getAttribute('data-sidebar-index');

      button.addEventListener('mouseenter', () => {
        this.showContentForSidebarItem(sidebarIndex);
      });

      button.addEventListener('click', () => {
        this.showContentForSidebarItem(sidebarIndex);
      });
    });
  }

  hideAllPromos() {
    this.querySelectorAll('.mega-menu__nav-2-promo-wrapper').forEach(wrapper => {
      wrapper.style.display = 'none';
    });
  }

  hideAllHeadings() {
    this.querySelectorAll('.mega-menu__shop-by-list').forEach(list => {
      list.style.display = 'none';
      list.classList.add('mega-menu__shop-by-list--hidden');
    });

    this.querySelectorAll('.mega-menu__heading-item').forEach(item => {
      item.style.display = 'none';
      item.classList.add('mega-menu__heading-item--hidden');
    });
  }

  showContentForSidebarItem(sidebarIndex) {
    if (!sidebarIndex) return;

    this.querySelectorAll('.mega-menu__nav-sidebar-link').forEach(link => {
      link.classList.toggle(
        'mega-menu__nav-sidebar-link--active',
        link.getAttribute('data-sidebar-index') === sidebarIndex
      );
    });

    this.hideAllPromos();

    const matchingPromo = this.querySelector(`.mega-menu__nav-2-promo-wrapper[data-sidebar-promo="${sidebarIndex}"]`);
    const headingItemsList = this.querySelector('.mega-menu__heading-items-list');
    
    if (matchingPromo) {
      matchingPromo.style.display = '';
      
      // Toggle text-only-promo class based on active promo wrapper
      const isTextOnly = matchingPromo.classList.contains('mega-menu__nav-2-promo-wrapper--text-only');

      if (headingItemsList) {
        headingItemsList.classList.toggle('mega-menu__heading-items-list--text-only-promo', isTextOnly);
      }
    } else if (headingItemsList) {
      headingItemsList.classList.remove('mega-menu__heading-items-list--text-only-promo');
    }

    // Get config for this sidebar item to handle heading visibility
    const sidebarConfig = this.getSidebarConfig(sidebarIndex);

    // If config has specific headingIds
    if (sidebarConfig && sidebarConfig.headingIds && sidebarConfig.headingIds.length > 0) {
      this.hideAllHeadings();
      
      sidebarConfig.headingIds.forEach(headingId => {
        const headingItem = this.querySelector(`[data-heading-id="${headingId}"]`);
        if (headingItem) {
          headingItem.style.display = '';
          headingItem.classList.remove('mega-menu__heading-item--hidden');
          
          const shopByList = headingItem.querySelector('.mega-menu__shop-by-list');
          if (shopByList) {
            shopByList.style.display = 'block';
            shopByList.classList.remove('mega-menu__shop-by-list--hidden');
          }
        }
      });
    } else {
      this.showAllHeadings();
    }

    this.activeSidebarIndex = sidebarIndex;
  }

  getSidebarConfig(sidebarIndex) {
    if (!this.config || !this.config.sidebarItems) return null;
    return this.config.sidebarItems.find(item => String(item.index) === String(sidebarIndex));
  }

  showAllHeadings() {
    this.querySelectorAll('.mega-menu__heading-item').forEach(item => {
      item.style.display = '';
      item.classList.remove('mega-menu__heading-item--hidden');
    });

    this.querySelectorAll('.mega-menu__shop-by-list').forEach(list => {
      list.style.display = 'block';
      list.classList.remove('mega-menu__shop-by-list--hidden');
    });
  }

  showAllContent() {
    this.showAllHeadings();

    this.querySelectorAll('.mega-menu__nav-2-promo-wrapper').forEach(wrapper => {
      wrapper.style.display = '';
    });
  }
}

if (!customElements.get('mega-menu-nav-filter')) {
  customElements.define('mega-menu-nav-filter', MegaMenuNavFilter);
}
